import { useEffect, useState } from "react";

import {
  Checkbox,
  Button,
  DatePicker,
  TableMetadata,
  Typography,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models/Export/ExportTypeEnum";
import { Row, Col, Container, Modal as BSModal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { saveImportantDates } from "../../api/ImportantDate/importantDateApi";
import {
  getSchoolYear,
  getSchoolYearExport,
} from "../../api/SchoolYear/SchoolYearApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ListTable,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { Role, ValidationFailure } from "../../models";
import {
  ImportantDate,
  UpdateImportantDate,
  UpdateEvent,
} from "../../models/ImportantDate";
import { SchoolYear } from "../../models/SchoolYear";
import { schoolYearListRoute } from "../../routes/routes";
import {
  resetIsEdited,
  setIsEdited,
  setSaveOK,
} from "../../slices/applicationSlice";

export function ImportantDateList() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [getSchoolYearData, setSchoolYearData] = useState<SchoolYear>();
  const [tableData, setTableData] = useState<ImportantDate[]>([]);
  const [editing, setEditing] = useState<ImportantDate | null>(null);
  const [getDateTimeFrom, setDateTimeFrom] = useState<Date>(new Date());
  const [getDateTimeTo, setDateTimeTo] = useState<Date>(new Date());
  const [getIsPublic, setIsPublic] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();

  const tableMetadata: TableMetadata<ImportantDate> = {
    rowKey: "eventId",
    columns: [
      {
        columnKey: "eventName",
        header: t("common.importantDateModel.eventName"),
        accessor: "eventName",
      },
      {
        columnKey: "description",
        header: t("common.importantDateModel.description"),
        accessor: "description",
      },
      {
        columnKey: "dateTimeFrom",
        header: t("common.importantDateModel.dateFrom"),
        accessor: (item: ImportantDate) => {
          return (
            <div className={"text-nowrap text-right"}>
              {hrDateWithTime(new Date(item.dateTimeFrom))}
            </div>
          );
        },
      },
      {
        columnKey: "dateTimeTo",
        header: t("common.importantDateModel.dateTo"),
        accessor: (item: ImportantDate) => {
          return (
            <div className={"text-nowrap text-right"}>
              {hrDateWithTime(new Date(item.dateTimeTo))}
            </div>
          );
        },
      },
      {
        columnKey: "isPublic",
        header: t("common.importantDateModel.isPublic"),
        accessor: (d: ImportantDate) => {
          return (
            <div className={"d-flex align-center"}>
              <Label
                color={d.isPublic ? "success" : "primary"}
                className="text-center"
                outline={false}
              >
                {d.isPublic ? t("common.yes") : t("common.no")}
              </Label>
            </div>
          );
        },
      },
      {
        columnKey: "edit",
        header: "-",
        accessor: (d: ImportantDate) => {
          return (
            <div className={"text-right"}>
              <Button
                size="sm"
                outline={true}
                color={
                  errors?.find((v) =>
                    v.propertyName?.startsWith(
                      "ImportantDates[" + tableData.indexOf(d) + "]"
                    )
                  ) != undefined
                    ? "danger"
                    : "primary"
                }
                disabled={
                  !getSchoolYearData?.isCurrent ||
                  !(
                    auth.currentRole?.role === Role.CAS ||
                    auth.currentRole?.role === Role.Helpdesk
                  )
                }
                onClick={() => handleDetailsClicked(d.eventId)}
              >
                {t("common.edit")}
              </Button>
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };

  useEffect(() => {
    (async () => {
      var result = (await getSchoolYear(+id)) as SchoolYear;
      if (result as SchoolYear) {
        setSchoolYearData(result);
        if (result.importantDates && result.importantDates.length > 0) {
          setTableData(result.importantDates);
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: tableData,
    pagination: false,
    paginationPage: undefined,
    paginationPageSize: undefined as any,
    totalItems: 1,
    defaultSort: {},
  });

  const newMetadata = {
    ...tableProps.metadata,
    columns: [...tableProps.metadata.columns],
  };

  const hrDateWithTime = (date: Date) => {
    if (new Date(date).getFullYear() !== 1) {
      return new Date(date).toLocaleString("hr-HR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return "-";
    }
  };

  const goBack = () => {
    history.push(schoolYearListRoute);
  };

  const handleDetailsClicked = (eventId: number) => {
    var importantDate = tableData.find((value) => value.eventId === eventId);
    if (importantDate) {
      if (new Date(importantDate.dateTimeFrom).getFullYear() !== 1)
        setDateTimeFrom(new Date(importantDate.dateTimeFrom));
      if (new Date(importantDate.dateTimeTo).getFullYear() !== 1)
        setDateTimeTo(new Date(importantDate.dateTimeTo));
      setIsPublic(importantDate.isPublic);
      setEditing(importantDate);
    }
  };

  const handleBackClicked = () => {
    goBack();
  };

  const handleSaveClicked = () => {
    (async () => {
      const updateEvent: UpdateEvent[] = [];
      tableData.forEach((element) => {
        if (element.isEdited) {
          updateEvent.push(element);
        }
      });
      const updateImportantDate: UpdateImportantDate = {
        schoolYearId: +id,
        updateEventList: updateEvent,
      };
      var result = await saveImportantDates(updateImportantDate);
      if (result) {
        setErrors(result.validationFailures);
        if (result.isValid) {
          dispatch(resetIsEdited());
          dispatch(setSaveOK());
          history.push({
            pathname: schoolYearListRoute,
          });
        }
      }
    })();
  };

  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    await getSchoolYearExport(+id, exportType);
  };

  return (
    <ContentArea>
      <ToolbarTop
        onClickBack={handleBackClicked}
        showBack="show"
        pageTitle={
          t("navigation.importantDateList") +
          " - " +
          (getSchoolYearData?.name ?? t("common.noData"))
        }
        errors={errors}
      ></ToolbarTop>
      <>
        <ListTable
          tableProps={tableProps}
          onClickDownload={(exportType: ExportTypeEnum) =>
            handleDownloadClicked(exportType)
          }
          showDownload
        />
        <br />
        <ToolbarEdit
          showSave={"show"}
          showCancel={"show"}
          onClickSave={handleSaveClicked}
          onClickCancel={handleBackClicked}
        ></ToolbarEdit>
        <BSModal
          size="lg"
          backdrop="static"
          centered
          animation={false}
          show={editing != null}
          onConfirm={() => {
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
          onHide={() => setEditing(null)}
        >
          <BSModal.Header>
            <Typography component="span" variant="largeBold">
              {editing?.eventName}
            </Typography>
          </BSModal.Header>
          <BSModal.Body>
            <Container>
              <Row>
                <Col md="6">
                  <DatePicker
                    label={t("common.importantDateModel.dateFrom")}
                    name={"item.dateFrom"}
                    value={getDateTimeFrom}
                    onChange={(e) => setDateTimeFrom(e.target.value)}
                    showTimePicker={true}
                    minYear={new Date().getFullYear() - 1}
                    className="mt-2"
                  />
                </Col>
                <Col md="6">
                  <DatePicker
                    label={t("common.importantDateModel.dateTo")}
                    name={"item.dateTo"}
                    value={getDateTimeTo}
                    onChange={(e) => setDateTimeTo(e.target.value)}
                    showTimePicker={true}
                    minYear={new Date().getFullYear() - 1}
                    className="mt-2"
                  />
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <Checkbox
                    label={t("common.importantDateModel.isPublic")}
                    name={"importantDate.isPublic"}
                    checked={getIsPublic}
                    onChange={() => setIsPublic(!getIsPublic)}
                  />
                </Col>
              </Row>
            </Container>
          </BSModal.Body>
          <BSModal.Footer>
            <Button size="sm" outline={true} onClick={() => setEditing(null)}>
              {t("common.quit")}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                editing!.dateTimeFrom = new Date(getDateTimeFrom);
                editing!.dateTimeTo = new Date(getDateTimeTo);
                editing!.isPublic = getIsPublic;
                editing!.isEdited = true;
                dispatch(setIsEdited());
                setEditing(null);
              }}
              disabled={getDateTimeFrom >= getDateTimeTo}
            >
              {t("common.approve")}
            </Button>
          </BSModal.Footer>
        </BSModal>
      </>
    </ContentArea>
  );
}
